import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Calendar as CalendarType } from '../types/calendar';
import { bannerPresets } from '../lib/banner/presets';
import { ArrowLeft, Settings as SettingsIcon, Palette, CreditCard, Trash2, Bell } from 'lucide-react';
import { toast } from '../utils/toast';
import DeleteCalendarModal from '../components/modals/DeleteCalendarModal';
import CalendarLinksSection from '../components/settings/CalendarLinksSection';
import BasicSettingsForm from '../components/settings/BasicSettingsForm';
import InviteSection from '../components/settings/InviteSection';
import PaymentOptionsSection from '../components/settings/PaymentOptionsSection';
import NotifySubscribersSection from '../components/settings/NotifySubscribersSection';
import { validateCalendarForm } from '../utils/calendarValidation';
import SEO from '../components/SEO';
import { getBannerPattern } from '../lib/banner/utils';
import { getGoogleToken, generateOAuthState } from '../utils/googleAuth';
import { GOOGLE_API_CONFIG } from '../config/google';

type Tab = 'settings' | 'style' | 'payment' | 'notifications';

const REDIRECT_URI = `${window.location.origin}/google-callback.html`;
const SETTINGS_RETURN_KEY = 'calendar_settings_return_id';

export default function CalendarSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [calendar, setCalendar] = useState<CalendarType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<string>('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [demoVideoUrl, setDemoVideoUrl] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [physicalAddress, setPhysicalAddress] = useState('');
  const [addressVisibility, setAddressVisibility] = useState<'public' | 'subscribers' | 'private'>('subscribers');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<Tab>('settings');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ synced: number; failed: number } | null>(null);
  const [googleCalendarId, setGoogleCalendarId] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchCalendar();
    }
  }, [id]);

  const fetchCalendar = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('calendars')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setCalendar(data);
      setName(data.name || '');
      setDescription(data.description || '');
      setSelectedBanner(data.banner?.id || bannerPresets[0].id);
      setCategoryId(data.category_id || '');
      setDemoVideoUrl(data.demo_video_url || '');
      setIsPublic(data.is_public);
      setPhysicalAddress(data.physical_address || '');
      setAddressVisibility(data.address_visibility || 'subscribers');
      setGoogleCalendarId(data.google_calendar_id || null);
    } catch (error) {
      console.error('Error fetching calendar:', error);
      toast.error('Failed to load calendar settings');
    } finally {
      setLoading(false);
    }
  };

  const createAndConnectGoogleCalendar = useCallback(async (googleToken: string, calendarId: string, calendarName: string, calendarDesc: string) => {
    const createResponse = await fetch('https://www.googleapis.com/calendar/v3/calendars', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${googleToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        summary: calendarName,
        description: calendarDesc,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    });

    if (!createResponse.ok) {
      throw new Error('Failed to create Google Calendar');
    }

    const googleCal = await createResponse.json();
    const gCalId: string = googleCal.id;

    // Make calendar public via ACL
    await fetch(`https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(gCalId)}/acl`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${googleToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role: 'reader', scope: { type: 'default' } }),
    });

    const icsUrl = `https://calendar.google.com/calendar/ical/${encodeURIComponent(gCalId)}/public/basic.ics`;

    // Persist to database
    const { error: updateError } = await supabase
      .from('calendars')
      .update({
        google_calendar_id: gCalId,
        google_calendar_url: icsUrl,
      })
      .eq('id', calendarId)
      .eq('user_id', user!.id);

    if (updateError) throw updateError;

    setGoogleCalendarId(gCalId);
    return gCalId;
  }, [user]);

  const syncPendingEvents = useCallback(async (calendarId: string, gCalId: string) => {
    const googleToken = await getGoogleToken();
    if (!googleToken) return;

    const { count } = await supabase
      .from('calendar_events')
      .select('id', { count: 'exact', head: true })
      .eq('calendar_id', calendarId)
      .eq('sync_status', 'pending_google_sync');

    if (!count || count === 0) return;

    setIsSyncing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-events-to-google`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            calendar_id: calendarId,
            google_access_token: googleToken,
            google_calendar_id: gCalId,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSyncResult({ synced: result.synced_count ?? 0, failed: result.failed_count ?? 0 });
        if (result.synced_count > 0) {
          toast.success(`${result.synced_count} event${result.synced_count !== 1 ? 's' : ''} synced to Google Calendar`);
        }
        if (result.failed_count > 0) {
          toast.error(`${result.failed_count} event${result.failed_count !== 1 ? 's' : ''} failed to sync`);
        }
      }
    } catch (syncError) {
      console.error('Error syncing pending events:', syncError);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // Handle return from OAuth redirect
  useEffect(() => {
    const returnCalendarId = sessionStorage.getItem(SETTINGS_RETURN_KEY);
    if (!returnCalendarId || returnCalendarId !== id) return;

    const googleToken = localStorage.getItem('google_token');
    if (!googleToken) return;

    sessionStorage.removeItem(SETTINGS_RETURN_KEY);

    // Wait for calendar to be fetched before proceeding
    const doConnect = async () => {
      // Re-fetch calendar to get latest state
      const { data } = await supabase
        .from('calendars')
        .select('*')
        .eq('id', returnCalendarId)
        .single();

      if (!data) return;

      const existingGCalId = data.google_calendar_id;

      if (existingGCalId) {
        // Already connected - just sync
        await syncPendingEvents(returnCalendarId, existingGCalId);
      } else {
        // Need to create a new Google Calendar
        setIsConnecting(true);
        try {
          const gCalId = await createAndConnectGoogleCalendar(
            googleToken,
            returnCalendarId,
            data.name,
            data.description || ''
          );
          toast.success('Google Calendar created and connected');
          await syncPendingEvents(returnCalendarId, gCalId);
        } catch (err) {
          console.error('Error connecting Google Calendar after OAuth:', err);
          toast.error('Failed to create Google Calendar. Please try again.');
        } finally {
          setIsConnecting(false);
        }
      }
    };

    doConnect();
  }, [id, createAndConnectGoogleCalendar, syncPendingEvents]);

  const handleGoogleConnect = async () => {
    if (!id) return;
    setIsConnecting(true);

    // Check if we already have a valid token
    const existingToken = await getGoogleToken();
    if (existingToken) {
      // We have a token - create the calendar directly
      try {
        const { data } = await supabase
          .from('calendars')
          .select('name, description')
          .eq('id', id)
          .maybeSingle();

        if (data) {
          const gCalId = await createAndConnectGoogleCalendar(
            existingToken,
            id,
            data.name,
            data.description || ''
          );
          toast.success('Google Calendar created and connected');
          await syncPendingEvents(id, gCalId);
        }
      } catch (err) {
        console.error('Error creating Google Calendar:', err);
        toast.error('Failed to create Google Calendar. Please try again.');
      } finally {
        setIsConnecting(false);
      }
      return;
    }

    // No token — do OAuth
    sessionStorage.setItem(SETTINGS_RETURN_KEY, id);
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.append('client_id', GOOGLE_API_CONFIG.CLIENT_ID);
    authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.append('response_type', 'token');
    authUrl.searchParams.append('scope', 'https://www.googleapis.com/auth/calendar');
    authUrl.searchParams.append('prompt', 'consent');
    authUrl.searchParams.append('state', generateOAuthState());
    window.location.href = authUrl.toString();
  };

  const handleGoogleReconnect = async () => {
    if (!id) return;
    setIsConnecting(true);
    sessionStorage.setItem(SETTINGS_RETURN_KEY, id);
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.append('client_id', GOOGLE_API_CONFIG.CLIENT_ID);
    authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.append('response_type', 'token');
    authUrl.searchParams.append('scope', 'https://www.googleapis.com/auth/calendar');
    authUrl.searchParams.append('prompt', 'consent');
    authUrl.searchParams.append('state', generateOAuthState());
    window.location.href = authUrl.toString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!calendar || !user?.id) return;

    const errors = validateCalendarForm({
      name,
      description,
      googleCalendarUrl: '',
      categoryId,
      demoVideoUrl,
      skipUrlValidation: true,
    });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const banner = bannerPresets.find(b => b.id === selectedBanner);
      if (!banner) throw new Error('Invalid banner selected');

      const nameChanged = name.trim() !== calendar.name;

      const { error } = await supabase
        .from('calendars')
        .update({
          banner,
          name: name.trim(),
          description: description.trim() || null,
          category_id: categoryId,
          demo_video_url: demoVideoUrl.trim() || null,
          is_public: isPublic,
          physical_address: physicalAddress.trim() || null,
          address_visibility: addressVisibility,
        })
        .eq('id', calendar.id)
        .eq('user_id', user.id);

      if (error) throw error;

      if (nameChanged) {
        const googleToken = localStorage.getItem('google_token');
        if (googleToken && googleCalendarId) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-google-calendar-name`,
                {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    calendar_id: calendar.id,
                    new_name: name.trim(),
                    google_access_token: googleToken,
                  }),
                }
              );
            }
          } catch (googleError) {
            console.error('Error updating Google Calendar name:', googleError);
          }
        }
      }

      toast.success('Settings saved successfully');
      navigate(`/calendar/${calendar.id}`);
    } catch (error) {
      console.error('Error updating calendar:', error);
      toast.error('Failed to save settings');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!calendar || !user) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('calendars')
        .delete()
        .eq('id', calendar.id)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Calendar deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting calendar:', error);
      toast.error('Failed to delete calendar');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!calendar || (user?.id !== calendar.user_id)) {
    navigate(calendar ? `/calendar/${calendar.id}` : '/dashboard', { replace: true });
    return null;
  }

  const isGoogleConnected = !!localStorage.getItem('google_token') && !!googleCalendarId;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Calendar Settings"
        description="Manage your calendar settings and preferences"
        noindex={true}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex justify-between items-center">
          <button
            type="button"
            onClick={() => navigate(`/calendar/${calendar.id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Calendar
          </button>
        </div>

        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {(['settings', 'style', 'payment', 'notifications'] as Tab[]).map((tab) => {
                const icons = {
                  settings: <SettingsIcon className="h-5 w-5 inline-block mr-2" />,
                  style: <Palette className="h-5 w-5 inline-block mr-2" />,
                  payment: <CreditCard className="h-5 w-5 inline-block mr-2" />,
                  notifications: <Bell className="h-5 w-5 inline-block mr-2" />,
                };
                const labels = {
                  settings: 'Settings',
                  style: 'Style',
                  payment: 'Payment',
                  notifications: 'Notifications',
                };
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {icons[tab]}
                    {labels[tab]}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="space-y-6">
          {activeTab === 'settings' && (
            <>
              <BasicSettingsForm
                name={name}
                description={description}
                categoryId={categoryId}
                demoVideoUrl={demoVideoUrl}
                isPublic={isPublic}
                physicalAddress={physicalAddress}
                addressVisibility={addressVisibility}
                validationErrors={validationErrors}
                googleCalendarId={googleCalendarId}
                isGoogleConnected={isGoogleConnected}
                isSyncing={isSyncing}
                isConnecting={isConnecting}
                syncResult={syncResult}
                onNameChange={setName}
                onDescriptionChange={setDescription}
                onCategoryChange={setCategoryId}
                onDemoVideoUrlChange={setDemoVideoUrl}
                onIsPublicChange={setIsPublic}
                onPhysicalAddressChange={setPhysicalAddress}
                onAddressVisibilityChange={setAddressVisibility}
                onGoogleConnect={handleGoogleConnect}
                onGoogleReconnect={handleGoogleReconnect}
                onSubmit={handleSubmit}
                saving={saving}
              />

              <InviteSection calendar={calendar} />

              {calendar && user && (
                <CalendarLinksSection
                  calendarId={calendar.id}
                  userId={user.id}
                  initialLinks={{
                    Twitch: calendar.streaming_urls?.Twitch,
                    YouTube: calendar.streaming_urls?.YouTube,
                    customUrl: calendar.custom_url
                  }}
                  onUpdate={fetchCalendar}
                />
              )}
            </>
          )}

          {activeTab === 'style' && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Banner Style</h3>
              <div className="grid grid-cols-2 gap-4">
                {bannerPresets.map((banner) => (
                  <button
                    key={banner.id}
                    type="button"
                    onClick={() => setSelectedBanner(banner.id)}
                    className={`relative rounded-lg p-4 flex flex-col items-center border-2 transition-all ${
                      selectedBanner === banner.id
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div
                      className="w-full h-24 rounded-md mb-2"
                      style={{
                        backgroundColor: banner.color,
                        backgroundImage: getBannerPattern(banner.pattern),
                        color: banner.textColor
                      }}
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {banner.name}
                    </span>
                  </button>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <PaymentOptionsSection
              calendar={calendar}
              userId={user.id}
              onUpdate={fetchCalendar}
            />
          )}

          {activeTab === 'notifications' && (
            <NotifySubscribersSection
              calendarId={calendar.id}
              calendarName={calendar.name}
            />
          )}

          <div className="border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center px-4 py-2 text-red-600 hover:text-red-700 border border-red-200 rounded-md hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Calendar
            </button>
          </div>
        </div>
      </div>

      <DeleteCalendarModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        calendarName={name}
        isDeleting={isDeleting}
      />
    </div>
  );
}
